<template>
  <div>
    <div v-if="!isReadOnly" class="row mt-4">
      <div class="col-md-4">
        <div class="color-primary-4 font-14 font-weight-bold mb-1">
          {{ $t('RegionHeader.HeaderText') }}:
        </div>
        <custom-drop-down
          :placeholder="$t('Item-Empty')"
          :options="catalog.regions"
          fieldkey="id"
          fieldname="name"
          class="case-info__dropdown input__exteded"
          v-model="selectedLocation.region"
          @input="onChangeRegionHandler"
        />
      </div>
      <div class="col-md-4">
        <div class="color-primary-4 font-14 font-weight-bold mb-1">
          {{ $t('lblWizarStep3Municipality.Text') | capitalize }}:
        </div>
        <custom-drop-down
          :placeholder="$t('Item-Empty')"
          :options="catalog.municipalities"
          fieldkey="id"
          fieldname="name"
          class="case-info__dropdown input__exteded"
          v-model="selectedLocation.municipality"
          @input="onChangeMunicipalityHandler"
        />
      </div>
      <div class="col-md-4">
        <div class="color-primary-4 font-14 font-weight-bold mb-1">
          {{ $t('lblWizarStep3SchoolHeader.HeaderText') | capitalize }}:
        </div>
        <custom-drop-down
          :placeholder="$t('Item-Empty')"
          :options="schoolFiltered"
          fieldkey="id"
          fieldname="name"
          class="case-info__dropdown input__exteded"
          v-model="selectedLocation.school"
        />
      </div>
      <div class="col-md-12 d-flex justify-content-end mt-3">
        <action-button
          class="btn-success px-4 py-2"
          :text="`${$options.filters.capitalize($t('lblWizarStep3Add.text'))}`"
          i="fal fa-plus-circle"
          @click="onAddHandler"
        />
      </div>
    </div>
    <div class="bg-gray-21 rounded p-3 mt-3">
      <div class="row">
        <div class="col-md-6 d-flex align-items-center">
          <span class="color-black-1 font-14 mr-2">{{ $t('Show') }}</span>
          <custom-drop-down
            class="drop__show-options"
            :options="catalog.showOptions"
            fieldkey="id"
            fieldname="name"
            v-model="locationSelectedShowOption"
            :clearable="false"
            @input="onSearchLocationHandler"
          />
          <span class="color-black-1 font-14 ml-2">
            {{ $t('Entries') }}
          </span>
        </div>
        <div class="col-md-6 d-flex justify-content-end">
          <custom-input
            v-model="locationSearchText"
            :placeholder="$t('Search')"
            ref="fastSearchInput"
            prepend-icon="fa fa-search"
            prepend-icon-type="success"
            :prepend-icon-left="false"
            class="fast-filter__search-input w-50"
            :prependIconClick="onSearchLocationHandler"
          />
        </div>
      </div>
      <div
        class="row bg-white my-3 mx-0 p-3 mb-3"
        v-for="(item, index) in paginationItemData"
        :key="`item-${index}`"
      >
        <div class="col-md-3">
          <div class="color-gray-18 font-14">
            {{ $t('RegionHeader.HeaderText') }}:
          </div>
          <span class="color-black font-14">{{ item.region.name }}</span>
        </div>
        <div class="col-md-3">
          <div class="color-gray-18 font-14">
            {{ $t('lblWizarStep3Municipality.Text') | capitalize }}:
          </div>
          <span class="color-black font-14">
            {{ item.municipality.name }}
          </span>
        </div>
        <div class="col-md-3">
          <div class="color-gray-18 font-14">
            {{ $t('lblWizarStep3SchoolHeader.HeaderText') | capitalize }}:
          </div>
          <span class="color-black font-14">{{ item.school.name }}</span>
        </div>
        <div
          v-if="!isReadOnly"
          class="col-md-3 d-flex justify-content-end align-items-center"
        >
          <action-button
            class="btn-outline-success delete-action"
            i="fal fa-minus-circle"
            @click="deleteItemHandler(item.id)"
          />
        </div>
      </div>
      <pagination
        :show-text="false"
        @pagination-go-page="onGoPageHandler"
        :data="paginationData"
      />
      <p class="badge-school">
        {{ $t('TotalSchoolsCount') }}:
        <span class="badge badge-pill badge-info">
          {{ this.itemsCopy ? itemsCopy.length : 0 }}
        </span>
      </p>
    </div>
  </div>
</template>

<script>
//Third Party
import _ from 'lodash';

//libs
import CustomInput from 'CustomInput';
import CustomDropDown from 'custom-drop-down';
import ActionButton from 'ActionButton';
import Pagination from 'pagination';

//services
import {
  getRegions as _getRegions,
  getMunicipalitiesByRegionId as _getMunicipalitiesByRegionId,
  getSchoolsByMunicipalityId as _getSchoolsByMunicipalityId,
} from '@/services/code.api.js';

//constants
const SHOW_OPTIONS = [
  { id: 5, name: '5' },
  { id: 10, name: '10' },
  { id: 15, name: '15' },
];
const DEFAULT_SELECTED_SHOW_OPTION = SHOW_OPTIONS[0];

export default {
  name: 'SchoolList',
  components: {
    CustomDropDown,
    CustomInput,
    ActionButton,
    Pagination,
  },
  props: {
    info: {
      type: Array,
      default: () => [],
    },
    isReadOnly: {
      type: Boolean,
      default: false,
    },
  },
  watch: {
    info: {
      immediate: true,
      deep: true,
      handler(newVal) {
        if (newVal) {
          this.items = [...newVal];
          this.itemsCopy = [...newVal];

          if (newVal?.length) {
            this.emitSchoolIds();
          }
        }
      },
    },
  },
  data: () => ({
    catalog: {
      regions: [],
      municipalities: [],
      schools: [],
      showOptions: [...SHOW_OPTIONS],
    },
    selectedLocation: {
      region: null,
      municipality: null,
      school: null,
    },
    locationSelectedShowOption: DEFAULT_SELECTED_SHOW_OPTION,
    locationSearchText: null,
    items: [],
    itemsCopy: [],
    pageIndex: 1,
  }),
  computed: {
    paginationData() {
      const perPage = this.paginationPerPage;
      const data = this.paginationItemData;

      return {
        data: data,
        currentPage: this.pageIndex,
        pageCount: this.pageCount,
        count: perPage,
        totalItemCount: data.length,
      };
    },
    pageCount() {
      const perPage = this.paginationPerPage;
      const filteredItems = this.locationSearchText
        ? this.itemsCopy.filter((item) => {
            const lowerCaseSearchValue = this.locationSearchText.toLowerCase();
            return (
              item?.region?.name.toLowerCase().includes(lowerCaseSearchValue) ||
              item?.municipality?.name
                .toLowerCase()
                .includes(lowerCaseSearchValue) ||
              item?.school?.name.toLowerCase().includes(lowerCaseSearchValue)
            );
          })
        : [...this.itemsCopy];

      return Math.ceil(filteredItems.length / perPage);
    },
    paginationItemData() {
      const perPage = this.paginationPerPage;
      const searchValue = this.locationSearchText?.toLowerCase();
      const filteredItems = searchValue
        ? this.itemsCopy.filter((item) =>
            ['region', 'municipality', 'school'].some((prop) =>
              item?.[prop]?.name.toLowerCase().includes(searchValue)
            )
          )
        : [...this.itemsCopy];

      const startIndex = (this.pageIndex - 1) * perPage;
      const endIndex = startIndex + perPage;

      return filteredItems.slice(startIndex, endIndex);
    },
    paginationPerPage() {
      return this.locationSelectedShowOption?.id ?? 5;
    },
    schoolFiltered() {
      return this.catalog.schools.filter(
        (school) =>
          !this.items.some(
            (item) => item.school.id === school.id || item.id === school.id
          ) &&
          !this.itemsCopy.some(
            (item) => item.school.id === school.id || item.id === school.id
          )
      );
    },
  },
  async mounted() {
    if (!this.isReadOnly) await this.getRegions();
  },
  methods: {
    async getRegions() {
      this.catalog.regions = (await _getRegions()).data;
    },
    async onChangeRegionHandler(value) {
      this.selectedLocation = {
        ...this.selectedLocation,
        municipality: null,
        school: null,
      };
      this.catalog = {
        ...this.catalog,
        municipalities: [],
        schools: [],
      };
      if (value?.id) {
        this.catalog.municipalities = (
          await _getMunicipalitiesByRegionId(value.id)
        ).data;
      }
    },
    async onChangeMunicipalityHandler(value) {
      this.selectedLocation = {
        ...this.selectedLocation,
        school: null,
      };
      this.catalog = {
        ...this.catalog,
        schools: [],
      };
      if (value?.id) {
        this.catalog.schools = (
          await _getSchoolsByMunicipalityId(value.id)
        ).data;
      }
    },
    onSearchLocationHandler() {
      this.pageIndex = 1;
      const searchValue = this.locationSearchText?.toLowerCase();
      const showOption = this.locationSelectedShowOption?.id
        ? this.locationSelectedShowOption?.id
        : Number.MAX_VALUE;

      if (searchValue) {
        const filteredItems = _.filter(this.itemsCopy, (item) => {
          return (
            item?.region?.name.toLowerCase().includes(searchValue) ||
            item?.municipality?.name.toLowerCase().includes(searchValue) ||
            item?.school?.name.toLowerCase().includes(searchValue)
          );
        });
        this.items = filteredItems.slice(0, showOption);
      } else {
        this.items = this.itemsCopy.slice(0, showOption);
      }
    },
    onAddHandler() {
      const selectedRegion = this.selectedLocation.region;
      const selectedMunicipality = this.selectedLocation.municipality;
      const selectedSchool = this.selectedLocation.school;

      if (
        selectedRegion?.id &&
        selectedMunicipality?.id &&
        selectedSchool?.id
      ) {
        const schoolAlreadyAdded = this.itemsCopy.some(
          (x) => x.school?.id === selectedSchool.id
        );
        if (!schoolAlreadyAdded) {
          const itemToAdd = {
            id: _.uniqueId(),
            region: selectedRegion,
            municipality: selectedMunicipality,
            school: selectedSchool,
          };

          this.items.push(itemToAdd);
          this.itemsCopy.push(itemToAdd);

          this.selectedLocation.school = null;

          // Perform filter items to apply show option value
          this.onSearchLocationHandler();
          this.emitSchoolIds();
        }
      }
    },
    deleteItemHandler(identifier) {
      if (identifier) {
        this.items = [...this.items.filter((x) => x.id !== identifier)];
        this.itemsCopy = [...this.itemsCopy.filter((x) => x.id !== identifier)];

        this.locationSearchText = null;
        this.onSearchLocationHandler();
        this.emitSchoolIds();
      }
    },
    emitSchoolIds() {
      const schoolIds = this.itemsCopy.map((x) => x.school.id);
      const schoolsData = this.itemsCopy;

      this.$emit('input', { schoolIds, schoolsData });
    },
    onGoPageHandler(page) {
      this.pageIndex = page;
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/scss/components/action-button/index.scss';

::v-deep .delete-action {
  border-radius: 4px;
  border: 1px solid #0f556a;
  width: 38px;
  height: 38px;
  display: flex;
  justify-content: center;
  align-items: center;
  &:hover {
    background: unset;
    color: unset;
  }
  i {
    font-size: 20px;
    margin-right: 0 !important;
  }
}

::v-deep .drop__show-options {
  width: 98px;
}

.badge-school {
  font-size: 16px !important;
  color: #6a6a69 !important;
}
</style>
